// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, isValidElement, cloneElement } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import oldIconNames from './old-icon-names.json';

import '../../helpers/base.less';
import './Icon.less';

export const ICON_SIZE_16 = '16';
export const ICON_SIZE_24 = '24';
export const ICON_SIZE_32 = '32';
export const ICON_SIZE_48 = '48';
export const ICON_SIZE_64 = '64';
export const ICON_SIZE_96 = '96';
export const ICON_SIZE_128 = '128';
export const ICON_SIZE_192 = '192';

const oldIconSizes = {
    sm: ICON_SIZE_16,
    md: ICON_SIZE_32,
    lg: ICON_SIZE_48,
    xl: ICON_SIZE_64,
    xxl: ICON_SIZE_96,
};

export const NAMES = [
    'arrow-right',
    'arrow-left',
    'arrow-down',
    'arrow-up',
    'arrow-down-in',
    'arrow-up-in',
    'arrow-down-out',
    'arrow-up-out',
    'arrow-down-tray',
    'arrow-up-tray',
    'archive',
    'unarchive',
    'arrows-four-directions',
    'arrows-loop',
    'refresh',
    'reload',
    'chevron-right',
    'chevron-left',
    'chevron-down',
    'chevron-up',
    'circle-check-mark',
    'circle-check-mark-filled',
    'circle-info',
    'circle-info-filled',
    'circle-question-mark',
    'circle-question-mark-filled',
    'circle-exclamation-mark',
    'circle-exclamation-mark-filled',
    'triangle-exclamation-mark',
    'triangle-exclamation-mark-filled',
    'lock-open',
    'lock-closed',
    'eye',
    'eye-closed',
    'chain',
    'chain-broken',
    'folder-open',
    'folder-closed',
    'filter',
    'filter-check-mark',
    'stop',
    'start',
    'sleep',
    'power',
    'check-mark',
    'cross-mark',
    'plus',
    'circle-minus',
    'clean',
    'remove',
    'search',
    'pencil',
    'backup',
    'copy',
    'world',
    'globe',
    'plugins',
    'themes',
    'four-squares',
    'list',
    'menu',
    'three-dots-horizontal',
    'sliders',
    'gear',
    'clock',
    'shield',
    'share',
    'shopping-cart',
    'user',
    'site-page',
    'arrow-diagonal-out',
];

/**
 * `Icons` are small symbolic images used for two things: to help quickly find the right control on the screen and to
 * bring user attention to something that requires consideration. In the former case, `Icon` typically acts as a part
 * of a [Button](#!/Button) and helps differentiate this particular [Button](#!/Button) from others. `Icons` come in several
 * standard sizes.
 */
const Icon = ({ baseClassName, className, name, src, size, animation, rotate, flipHorizontal, flipVertical, thin, children, alt, ...props }) => {
    size = oldIconSizes[size] || size;

    return (
        <span
            className={classNames(
                baseClassName,
                {
                    [`${baseClassName}--size-${size}`]: size,
                    [`${baseClassName}--animation-${animation}`]: animation,
                    [`${baseClassName}--rotate-${rotate}`]: rotate,
                    [`${baseClassName}--flip-horizontal`]: flipHorizontal,
                    [`${baseClassName}--flip-vertical`]: flipVertical,
                },
                className
            )}
            {...props}
        >
            {name ? (
                <svg focusable="false"><use xlinkHref={`${require('./images/symbols.svg')}#${oldIconNames[name] || name}`} /></svg>
            ) : (
                (src && <img src={src} alt={alt} />) || children
            )}
        </span>
    );
};

Icon.propTypes = {
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * Name of the icon
     */
    name: PropTypes.oneOf(NAMES),
    /**
     * Url to custom image
     */
    src: PropTypes.string,
    /**
     * Size of the icon
     */
    size: PropTypes.oneOf([
        ICON_SIZE_16,
        ICON_SIZE_24,
        ICON_SIZE_32,
        ICON_SIZE_48,
        ICON_SIZE_64,
        ICON_SIZE_96,
        ICON_SIZE_128,
        ICON_SIZE_192,
    ]),
    /**
     * Animation for icon
     */
    animation: PropTypes.oneOf(['spin']),
    /**
     * Rotate by degree
     */
    rotate: PropTypes.oneOf(['90', '180', '270']),
    /**
     * Flip icon horizontal
     */
    flipHorizontal: PropTypes.bool,
    /**
     * Flip icon vertical
     */
    flipVertical: PropTypes.bool,
    /**
     * @ignore
     */
    thin: PropTypes.bool,
    /**
     * Alternative text for image
     */
    alt: PropTypes.string,
    /**
     * Content of the icon
     */
    children: PropTypes.node,
};
Icon.defaultProps = {
    baseClassName: `${CLS_PREFIX}icon`,
    className: null,
    name: null,
    src: null,
    size: null,
    animation: null,
    rotate: null,
    flipHorizontal: false,
    flipVertical: false,
    thin: false,
    alt: '',
    children: null,
};

const create = (icon, defaults = {}) => {
    if (!icon) {
        return icon;
    }

    if (isValidElement(icon)) {
        const { className, ...props } = icon.props;
        const newProps = { ...defaults };

        Object.keys(props).forEach(name => {
            if (props[name] !== null && props[name] !== undefined) {
                newProps[name] = props[name];
            }
        });

        if (defaults.className || className) {
            newProps.className = classNames(defaults.className, className);
        }

        return cloneElement(icon, newProps);
    }

    if (typeof icon === 'object') {
        const { className, ...props } = icon;

        return (
            <Icon
                {...defaults}
                className={defaults.className || className ? classNames(defaults.className, className) : null}
                {...props}
            />
        );
    }

    return (
        <Icon {...defaults} name={icon} />
    );
};

Icon.create = create;

export default Icon;



// WEBPACK FOOTER //
// ./components/Icon/Icon.js